import React, { useState, useEffect, useRef ,} from "react";
import {
  Box,
  Typography,
  useTheme,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Grid,
  Stack,
  Divider,
  Chip,
  Modal
  
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FilterListIcon from "@mui/icons-material/FilterList";

import { Formik, Field } from "formik";

const Complaints = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalComplaints, setTotalComplaints] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
 
  const {
    complaints,
    setComplaints,
    authorizedFetch,
    createComplaint,
    getComplaintByTicketNo,
    formatDate,
  } = useAuth();
  const limit = 10;
  const fetchTimeout = useRef(null);

  const statusOptions = ["", "open", "in_progress", "closed"];
  const priorityOptions = ["", "Low", "Medium", "High"];



  const columns = [
    { field: "order_id", headerName: "ID", minWidth: 50, flex: 0.4 },
    {
      field: "consumer_mobile",
      headerName: "Mobile Number",
      minWidth: 120,
      flex: 1,
    },
    {
      field: "complaint",
      headerName: "Complaint Name",
      minWidth: 150,
      flex: 1,
    },
    { field: "id", headerName: "Ticket No", minWidth: 150, flex: 1.5 },
    { field: "status", headerName: "Status", minWidth: 100, flex: 0.6 },
    { field: "priority", headerName: "Priority", minWidth: 100, flex: 1 },
    {
      field: "created_at",
      headerName: "Created At",
      minWidth: 150,
      flex: 1,
      renderCell: ({ value }) => formatDate(value),
    },
    {
      field: "view",
      headerName: "View",
      minWidth: 80,
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          onClick={() => {
                handleViewClick(params.row);
          }}
          sx={{ color: colors.blueAccent[300] }}
          title="View Complaint Details"
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ];


const [showImageModal, setShowImageModal] = useState(false);
const [selectedImage, setSelectedImage] = useState(null);

const handleImageClick = (imageUrl) => {
  setSelectedImage(imageUrl);
  setShowImageModal(true);
};

const handleCloseModal = () => {
  setShowImageModal(false);
  setSelectedImage(null);
};


  const fetchComplaintsWithParams = async (page) => {
    setSearchLoading(true);
    setSearchError(null);
    try {
      let query = `page=${page}&per_page=${limit}`;
      if (searchTerm.trim()) {
        query += `&search=${encodeURIComponent(searchTerm)}`;
      }
      if (statusFilter.length) {
        query += `&status=${encodeURIComponent(statusFilter)}`;
      }
      if (priorityFilter.length) {
        query += `&priority=${encodeURIComponent(priorityFilter)}`;
      }
      const response = await authorizedFetch(`/admin/complaints?${query}`);
      const complaintLoaded = await response.json();

      if (response.ok) {
        if (
          !complaintLoaded.complaints ||
          complaintLoaded.complaints.length === 0
        ) {
          setSearchError("No complaints found matching the criteria");
          setComplaints([]);
          setTotalPages(0);
          setTotalComplaints(0);
        } else {
          const total =
            complaintLoaded.pagination?.total_count ||
            complaintLoaded.complaints.length;
          setComplaints(
            complaintLoaded.complaints.map((complaint, index) => ({
              ...complaint,
              order_id: (page - 1) * limit + index + 1,
            }))
          );
          setTotalComplaints(total);
          setTotalPages(Math.ceil(total / limit));
          setSearchError(null);
        }
      } else {
        setSearchError(complaintLoaded.message || "Failed to fetch complaints");
        setComplaints([]);
        setTotalPages(0);
        setTotalComplaints(0);
      }
    } catch (error) {
      console.error("Error fetching complaints:", error);
      setSearchError("Failed to fetch complaints");
      setComplaints([]);
      setTotalPages(0);
      setTotalComplaints(0);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleViewClick = async (row) => {
    try {
      const complaint = await getComplaintByTicketNo(row.id);
      setSelectedComplaint({
        ...complaint,
        selectedStatus: complaint.status || "open",
        selectedPriority: complaint.priority || "Low",
      });
      setViewDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch complaint details:", error);
      setSnackbarMessage("Failed to load complaint details");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleAddComplaint = async (values, { resetForm }) => {
    try {
      await createComplaint(values);
      setAddDialogOpen(false);
      resetForm();
      await fetchComplaintsWithParams(1);
      setCurrentPage(1);
      setSnackbarMessage("Complaint created successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to create complaint:", error);
      setSnackbarMessage(error.message || "Failed to create complaint");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleAddComment = async (comment) => {
    if (!comment?.trim()) {
      setSnackbarMessage("Comment cannot be empty");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    try {
      const response = await authorizedFetch(
        `/complaint/${selectedComplaint.id}/comment`,
        {
          method: "POST",
          body: JSON.stringify({ comment: comment.trim() }),
        }
      );
      await response.json();
      const updatedComplaint = await getComplaintByTicketNo(
        selectedComplaint.id
      );
      setSelectedComplaint({
        ...updatedComplaint,
        selectedStatus: updatedComplaint.status || "open",
        selectedPriority: updatedComplaint.priority || "Low",
      });
      setSnackbarMessage("Comment added successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to add comment:", error);
      setSnackbarMessage(error.message || "Failed to add comment");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleStatusChange = async () => {
    if (!selectedComplaint?.selectedStatus) {
      setSnackbarMessage("Please select a status");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    try {
      const response = await authorizedFetch(
        `/complaint/${selectedComplaint.id}/status`,
        {
          method: "PUT",
          body: JSON.stringify({ status: selectedComplaint.selectedStatus }),
        }
      );
      await response.json();
      const updatedComplaint = await getComplaintByTicketNo(
        selectedComplaint.id
      );
      setSelectedComplaint({
        ...updatedComplaint,
        selectedStatus: updatedComplaint.status || "open",
        selectedPriority: updatedComplaint.priority || "Low",
      });
      setComplaints(
        complaints.map((c) =>
          c.id === updatedComplaint.id
            ? { ...c, status: updatedComplaint.status }
            : c
        )
      );
      setSnackbarMessage("Status updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to update status:", error);
      setSnackbarMessage(error.message || "Failed to update status");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handlePriorityChange = async () => {
    if (!selectedComplaint?.selectedPriority) {
      setSnackbarMessage("Please select a priority");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    try {
      const response = await authorizedFetch(
        `/complaint/${selectedComplaint.id}/status`,
        {
          method: "PUT",
          body: JSON.stringify({
            priority: selectedComplaint.selectedPriority,
          }),
        }
      );
      await response.json();
      const updatedComplaint = await getComplaintByTicketNo(
        selectedComplaint.id
      );
      setSelectedComplaint({
        ...updatedComplaint,
        selectedStatus: updatedComplaint.status || "open",
        selectedPriority: updatedComplaint.priority || "Low",
      });
      setComplaints(
        complaints.map((c) =>
          c.id === updatedComplaint.id
            ? { ...c, priority: updatedComplaint.priority }
            : c
        )
      );
      setSnackbarMessage("Priority updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to update priority:", error);
      setSnackbarMessage(error.message || "Failed to update priority");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    if (fetchTimeout.current) {
      clearTimeout(fetchTimeout.current);
    }
    fetchTimeout.current = setTimeout(() => {
      setCurrentPage(1);
      fetchComplaintsWithParams(1);
    }, 300);
    return () => {
      if (fetchTimeout.current) {
        clearTimeout(fetchTimeout.current);
      }
    };
  }, [searchTerm, statusFilter, priorityFilter]);

  const handleSearchClick = () => {
    if (fetchTimeout.current) {
      clearTimeout(fetchTimeout.current);
    }
    setCurrentPage(1);
    fetchComplaintsWithParams(1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchComplaintsWithParams(nextPage);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      fetchComplaintsWithParams(prevPage);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box m="10px">
      <Header
        title="COMPLAINTS MANAGEMENT"
        subtitle="Manage Consumer Complaints"
      />

      <Box display="flex" flexDirection="column" gap="15px" mb="20px">
        {/* Filter Tag */}

        {/* Search and Filter Row */}
        <Box display="flex" alignItems="center" gap="15px" flexWrap="wrap">
          {/* Search Field */}
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by Mobile No, IMEI, or Ticket No"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              minWidth: "300px",
              flex: "1 1 auto",
              "& .MuiOutlinedInput-root": {
                backgroundColor: colors.primary[400],
                "& fieldset": {
                  borderColor: colors.grey[100],
                },
                "&:hover fieldset": {
                  borderColor: colors.blueAccent[300],
                },
              },
              "& .MuiInputBase-input": {
                color: colors.grey[100],
              },
            }}
          />

          {/* Status Filter */}
          <FormControl sx={{ minWidth: "140px" }}>
            <InputLabel
              shrink
              sx={{
                color: colors.grey[100],
                "&.Mui-focused": {
                  color: colors.blueAccent[300],
                },
              }}
            >
              Status Filter
            </InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status Filter"
              notched
              size="small"
              sx={{
                backgroundColor: colors.primary[400],
                "& .MuiSelect-select": {
                  color: colors.grey[100],
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.grey[100],
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.blueAccent[300],
                },
                "& .MuiSelect-icon": {
                  color: colors.grey[100],
                },
              }}
            >
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status === ""
                    ? "None"
                    : status === "in_progress"
                    ? "In Progress"
                    : status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Priority Filter */}
          <FormControl sx={{ minWidth: "140px" }}>
            <InputLabel
              shrink
              sx={{
                color: colors.grey[100],
                "&.Mui-focused": {
                  color: colors.blueAccent[300],
                },
              }}
            >
              Priority Filter
            </InputLabel>
            <Select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              label="Priority Filter"
              notched
              size="small"
              sx={{
                backgroundColor: colors.primary[400],
                "& .MuiSelect-select": {
                  color: colors.grey[100],
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.grey[100],
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.blueAccent[300],
                },
                "& .MuiSelect-icon": {
                  color: colors.grey[100],
                },
              }}
            >
              {priorityOptions.map((priority) => (
                <MenuItem key={priority} value={priority}>
                  {priority === "" ? "None" : priority}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Action Buttons */}
          <Box display="flex" alignItems="center" gap="10px">
            {searchLoading && <CircularProgress size={20} color="primary" />}

            <Button
              variant="contained"
              color="primary"
              onClick={handleSearchClick}
              disabled={searchLoading}
              sx={{
                backgroundColor: colors.blueAccent[700],
                "&:hover": {
                  backgroundColor: colors.blueAccent[600],
                },
                textTransform: "none",
                fontSize: "0.875rem",
                padding: "8px 16px",
                minWidth: "80px",
              }}
            >
              {searchLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Search"
              )}
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={() => setAddDialogOpen(true)}
              sx={{
                backgroundColor: colors.greenAccent[600],
                "&:hover": {
                  backgroundColor: colors.greenAccent[500],
                },
                textTransform: "none",
                fontSize: "0.875rem",
                padding: "8px 16px",
              }}
            >
              Raise Complaint
            </Button>
          </Box>
        </Box>

        {/* Active Filters Display */}
        {(statusFilter || priorityFilter) && (
          <Box display="flex" alignItems="center" gap="10px" flexWrap="wrap">
            <Typography variant="body2" sx={{ color: colors.grey[300] }}>
              Active Filters:
            </Typography>
            {statusFilter && (
              <Chip
                label={`Status: ${
                  statusFilter === "in_progress" ? "In Progress" : statusFilter
                }`}
                onDelete={() => setStatusFilter("")}
                size="small"
                sx={{
                  backgroundColor: colors.blueAccent[700],
                  color: colors.grey[100],
                  "& .MuiChip-deleteIcon": {
                    color: colors.grey[100],
                  },
                }}
              />
            )}
            {priorityFilter && (
              <Chip
                label={`Priority: ${priorityFilter}`}
                onDelete={() => setPriorityFilter("")}
                size="small"
                sx={{
                  backgroundColor: colors.blueAccent[700],
                  color: colors.grey[100],
                  "& .MuiChip-deleteIcon": {
                    color: colors.grey[100],
                  },
                }}
              />
            )}
          </Box>
        )}
      </Box>
      {searchError && (
        <Typography color="error" mb="10px">
          {searchError}
        </Typography>
      )}
      <Box
        m="20px 0 0 0"
        height="70vh"
        sx={{
          width: "100%",
          overflow: "hidden", // Prevent any overflow on the container
          "& .MuiDataGrid-root": {
            border: "none",
            width: "100%",
            height: "100%",
            overflow: "hidden", // Prevent root overflow
          },
          "& .MuiDataGrid-main": {
            overflow: "hidden",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
            fontSize: "0.9rem",
            position: "sticky",
            top: 0,
            zIndex: 2,
            width: "100% !important", // Force full width
            minWidth: "100% !important",
            marginBottom: "-55px",
            // Remove the negative margin
          },
          "& .MuiDataGrid-columnHeadersInner": {
            backgroundColor: colors.blueAccent[700],
            width: "100% !important",
            // Compensate for scrollbar width
            paddingRight: "0px !important",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
            overflow: "auto !important",
            // Hide horizontal scrollbar
            overflowX: "hidden !important",
            overflowY: "auto !important",
            // Custom scrollbar styling
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: colors.primary[400],
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: colors.blueAccent[600],
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: colors.blueAccent[500],
            },
          },
          "& .MuiDataGrid-virtualScrollerContent": {
            height: "auto !important",
            width: "100% !important",
          },
          "& .MuiDataGrid-virtualScrollerRenderZone": {
            width: "100% !important",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            fontSize: "0.875rem",
            whiteSpace: "normal !important",
            wordWrap: "break-word !important",
            lineHeight: "1.4 !important",
            padding: "8px !important",
            overflow: "hidden", // Prevent cell content overflow
          },
          "& .MuiDataGrid-row": {
            width: "100% !important",
            maxWidth: "100% !important",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: colors.blueAccent[800],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
            position: "sticky",
            bottom: 0,
            zIndex: 1,
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            whiteSpace: "normal",
            lineHeight: "1.2",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          // Hide default scrollbars on the entire grid
          "& .MuiDataGrid-scrollArea": {
            display: "none",
          },
          "& .MuiDataGrid-scrollArea--left": {
            display: "none",
          },
          "& .MuiDataGrid-scrollArea--right": {
            display: "none",
          },
        }}
      >
        <DataGrid
          rows={complaints}
          columns={columns}
          disableSelectionOnClick
          disableExtendRowFullWidth
          hideFooterPagination
          hideFooterSelectedRowCount
          disableColumnResize // Prevent column resizing that might cause horizontal scroll
          disableColumnMenu // Optional: disable column menu
          sx={{
            "& .MuiDataGrid-columnHeader": {
              outline: "none !important",
            },
            "& .MuiDataGrid-cell": {
              outline: "none !important",
            },
          }}
        />
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap="10px"
        mt="10px"
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handlePrevPage}
          disabled={searchLoading || currentPage === 1}
          sx={{
            backgroundColor: colors.blueAccent[700],
            "&:hover": {
              backgroundColor: colors.blueAccent[600],
            },
            textTransform: "none",
          }}
        >
          Previous
        </Button>
        <Typography>
          Page {currentPage} of {totalPages} (Total Complaints:{" "}
          {totalComplaints})
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleNextPage}
          disabled={
            searchLoading || currentPage === totalPages || totalPages === 0
          }
          sx={{
            backgroundColor: colors.blueAccent[700],
            "&:hover": {
              backgroundColor: colors.blueAccent[600],
            },
            textTransform: "none",
          }}
        >
          Next
        </Button>
      </Box>

      {/* Add Complaint Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Complaint</DialogTitle>
        <Formik
          initialValues={{
            mobile_number: "",
            consumer_name: "",
            pump_imei: "",
            complaint: "",
            description: "",
            priority: "Low",
          }}
          onSubmit={handleAddComplaint}
        >
          {({ values, setFieldValue, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Field
                  as={TextField}
                  name="mobile_number"
                  label="Mobile Number"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  onChange={async (e) => {
                    const mobile = e.target.value;
                    setFieldValue("mobile_number", mobile);
                    if (mobile.length === 10) {
                      try {
                        const response = await authorizedFetch(
                          `/consumer/${mobile}`
                        );
                        const consumer = await response.json();
                        if (response.ok) {
                          setFieldValue("consumer_name", consumer.name || "");
                          setFieldValue(
                            "pump_imei",
                            consumer.imei_list?.[0] || ""
                          );
                        } else {
                          setSnackbarMessage("Mobile Number does not exist");
                          setSnackbarSeverity("error");
                          setSnackbarOpen(true);
                        }
                      } catch (error) {
                        console.error("Failed to fetch consumer:", error);
                        setFieldValue("consumer_name", "");
                        setFieldValue("pump_imei", "");
                      }
                    } else {
                      setFieldValue("consumer_name", "");
                      setFieldValue("pump_imei", "");
                    }
                  }}
                  sx={{ mb: 2 }}
                />
                <Field
                  as={TextField}
                  name="consumer_name"
                  label="Consumer Name"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  disabled
                  sx={{ mb: 2 }}
                />
                <Field
                  as={TextField}
                  name="pump_imei"
                  label="Pump IMEI"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  disabled
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Complaint Type</InputLabel>
                  <Field as={Select} name="complaint" label="Complaint Type">
                    <MenuItem value="Pump Malfunction">
                      Pump Malfunction
                    </MenuItem>
                    <MenuItem value="Connectivity Issues">
                      Connectivity Issues
                    </MenuItem>
                    <MenuItem value="Service Delivery Issue">
                      Service Delivery Issue
                    </MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Field>
                </FormControl>
                <Field
                  as={TextField}
                  name="description"
                  label="Description"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={3}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Priority</InputLabel>
                  <Field as={Select} name="priority" label="Priority">
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                  </Field>
                </FormControl>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setAddDialogOpen(false)}
                  color="secondary"
                >
                  Cancel
                </Button>
                <Button type="submit" color="primary" variant="contained">
                  Submit
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>

      {/* View Complaint Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: colors.blueAccent[700],
            color: colors.grey[100],
            py: 2,
          }}
        >
          Complaint Details
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: colors.primary[400], py: 3 }}>
          {selectedComplaint && (
            <Stack spacing={3}>
              {/* Complaint Details */}
              <Box>
                <Typography variant="h6" color={colors.grey[100]} mb={2}>
                  Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body1"
                      color={colors.grey[300]}
                      fontWeight="bold"
                    >
                      Ticket No
                    </Typography>
                    <Typography variant="body1" color={colors.grey[100]}>
                      {selectedComplaint.id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body1"
                      color={colors.grey[300]}
                      fontWeight="bold"
                    >
                      Consumer Name
                    </Typography>
                    <Typography variant="body1" color={colors.grey[100]}>
                      {selectedComplaint?.consumer?.name || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body1"
                      color={colors.grey[300]}
                      fontWeight="bold"
                    >
                      Mobile Number
                    </Typography>
                    <Typography variant="body1" color={colors.grey[100]}>
                      {selectedComplaint?.consumer?.mobile_number || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body1"
                      color={colors.grey[300]}
                      fontWeight="bold"
                    >
                      Farm Address
                    </Typography>
                    <Typography variant="body1" color={colors.grey[100]}>
                      {selectedComplaint?.consumer?.farm_address || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body1"
                      color={colors.grey[300]}
                      fontWeight="bold"
                    >
                      Complaint Type
                    </Typography>
                    <Typography variant="body1" color={colors.grey[100]}>
                      {selectedComplaint.complaint || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body1"
                      color={colors.grey[300]}
                      fontWeight="bold"
                    >
                      Status
                    </Typography>
                    <Typography variant="body1" color={colors.grey[100]}>
                      {selectedComplaint.status || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body1"
                      color={colors.grey[300]}
                      fontWeight="bold"
                    >
                      Priority
                    </Typography>
                    <Typography variant="body1" color={colors.grey[100]}>
                      {selectedComplaint.priority || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body1"
                      color={colors.grey[300]}
                      fontWeight="bold"
                    >
                      Created At
                    </Typography>
                    <Typography variant="body1" color={colors.grey[100]}>
                      {formatDate(selectedComplaint.created_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body1"
                      color={colors.grey[300]}
                      fontWeight="bold"
                    >
                      Pump IMEI
                    </Typography>
                    <Typography variant="body1" color={colors.grey[100]}>
                      {selectedComplaint.pump_imei || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="h4"
                      color={colors.grey[300]}
                      fontWeight="bold"
                    >
                      Description
                    </Typography>
                    <Typography variant="h5" color={colors.grey[100]}>
                      {selectedComplaint.description || "N/A"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* <Divider sx={{ borderColor: colors.grey[700] }} /> */}

              <Divider sx={{ borderColor: colors.grey[700] }} />

              {/* Photo Section in Details */}
               {selectedComplaint?.image_path && (
                
  <Typography variant="body1" color="textPrimary">
    <Typography
                      variant="h4"
                      color={colors.grey[300]}
                      fontWeight="bold"
                    >
                      Customer Pump Photo
                    </Typography>
    <img
      src={selectedComplaint.image_path}
      alt="Complaint"
      style={{
        width: "100%",
        height: 200,
        objectFit: "cover",
        borderRadius: 8,
        backgroundColor: "#f5f5f5",
        cursor: "pointer",
      }}
      onClick={() => handleImageClick(selectedComplaint.image_path)}
    />
    <div style={{ textAlign: 'center', fontSize: '0.8rem', color: '#888' }}>
      Tap to enlarge
    </div>
  </Typography>
)}

<Modal open={showImageModal} onClose={handleCloseModal}>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      bgcolor: 'background.paper',
      boxShadow: 24,
      p: 2,
      outline: 'none',
      maxHeight: '90vh',
      maxWidth: '90vw',
      overflow: 'auto',
    }}
  >
    {selectedImage && (
      <img
        src={selectedImage}
        alt="Full View"
        style={{
          width: '100%',
          height: 'auto',
          objectFit: 'contain',
          borderRadius: 8,
        }}
      />
    )}
  </Box>
</Modal>
               

              <Divider sx={{ borderColor: colors.grey[700] }} />
              {/* Status and Priority Actions */}
              <Box>
                <Typography variant="h6" color={colors.grey[100]} mb={2}>
                  Actions
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ color: colors.grey[300] }}>
                        Change Status
                      </InputLabel>
                      <Select
                        value={selectedComplaint.selectedStatus || "open"}
                        label="Change Status"
                        onChange={(e) =>
                          setSelectedComplaint({
                            ...selectedComplaint,
                            selectedStatus: e.target.value,
                          })
                        }
                        sx={{
                          backgroundColor: colors.primary[400],
                          "& .MuiSelect-select": {
                            color: colors.grey[100],
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: colors.grey[700],
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: colors.blueAccent[300],
                          },
                        }}
                      >
                        <MenuItem value="open">Open</MenuItem>
                        <MenuItem value="in_progress">In Progress</MenuItem>
                        <MenuItem value="closed">Closed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleStatusChange}
                      fullWidth
                      sx={{
                        backgroundColor: colors.blueAccent[700],
                        "&:hover": {
                          backgroundColor: colors.blueAccent[600],
                        },
                      }}
                    >
                      Change Status
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ color: colors.grey[300] }}>
                        Change Priority
                      </InputLabel>
                      <Select
                        value={selectedComplaint.selectedPriority || "Low"}
                        label="Change Priority"
                        onChange={(e) =>
                          setSelectedComplaint({
                            ...selectedComplaint,
                            selectedPriority: e.target.value,
                          })
                        }
                        sx={{
                          backgroundColor: colors.primary[400],
                          "& .MuiSelect-select": {
                            color: colors.grey[100],
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: colors.grey[700],
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: colors.blueAccent[300],
                          },
                        }}
                      >
                        <MenuItem value="Low">Low</MenuItem>
                        <MenuItem value="Medium">Medium</MenuItem>
                        <MenuItem value="High">High</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handlePriorityChange}
                      fullWidth
                      sx={{
                        backgroundColor: colors.blueAccent[700],
                        "&:hover": {
                          backgroundColor: colors.blueAccent[600],
                        },
                      }}
                    >
                      Change Priority
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {/* Comments Section */}
              <Box>
                <Typography variant="h6" color={colors.grey[100]} mb={2}>
                  Comments
                </Typography>
                <Box
                  sx={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    pr: 1,
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: colors.blueAccent[700],
                      borderRadius: "3px",
                    },
                  }}
                >
                  {selectedComplaint.comments &&
                  selectedComplaint.comments.length > 0 ? (
                    selectedComplaint.comments
                      .sort(
                        (a, b) =>
                          new Date(b.created_at) - new Date(a.created_at)
                      )
                      .map((comment, index) => (
                        <Box
                          key={index}
                          mb={1}
                          p={2}
                          bgcolor={colors.blueAccent[800]}
                          borderRadius="8px"
                          border={`1px solid ${colors.grey[700]}`}
                        >
                          <Typography variant="body2" color={colors.grey[100]}>
                            <strong>
                              {comment.role.toUpperCase() || "Unknown"}:
                            </strong>{" "}
                            {comment.comment}
                          </Typography>
                          <Typography variant="body2" color={colors.grey[300]}>
                            {formatDate(comment.created_at)}
                          </Typography>
                        </Box>
                      ))
                  ) : (
                    <Typography variant="body2" color={colors.grey[300]}>
                      No comments available
                    </Typography>
                  )}
                </Box>
                <Box mt={2}>
                  <TextField
                    label="Add Comment"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={2}
                    value={selectedComplaint.newComment || ""}
                    onChange={(e) =>
                      setSelectedComplaint({
                        ...selectedComplaint,
                        newComment: e.target.value,
                      })
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: colors.primary[400],
                        "& fieldset": {
                          borderColor: colors.grey[700],
                        },
                        "&:hover fieldset": {
                          borderColor: colors.blueAccent[300],
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: colors.grey[100],
                      },
                      "& .MuiInputLabel-root": {
                        color: colors.grey[300],
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      handleAddComment(selectedComplaint.newComment);
                      setSelectedComplaint({
                        ...selectedComplaint,
                        newComment: "",
                      });
                    }}
                    sx={{
                      mt: 1,
                      backgroundColor: colors.blueAccent[700],
                      "&:hover": {
                        backgroundColor: colors.blueAccent[600],
                      },
                    }}
                  >
                    Submit Comment
                  </Button>
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: colors.primary[400], py: 2 }}>
          <Button
            onClick={() => setViewDialogOpen(false)}
            color="secondary"
            variant="outlined"
            sx={{
              borderColor: colors.grey[700],
              color: colors.grey[100],
              "&:hover": {
                borderColor: colors.blueAccent[300],
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Complaints;
